import * as api from './addresses.api';

import type { CartShippingMethod } from 'domains/cart_shipping';
import type {
  Address,
  AddressValidationReturnValue,
  BaseAddress,
  CountryState,
  SmartyStreetResponse,
  ValidationAPIInputAddress,
  ValidationAPIOutputAddress,
} from './addresses.types';
import { IS_PRODUCTION } from 'utils/globals';
import reporter from 'utils/reporter';

function wosToValidation(addressData: BaseAddress): ValidationAPIInputAddress {
  const { address1, address2, city, state, zip, country } = addressData;
  return { add1: address1, add2: address2, city, state, zip, country };
}

function validationToWos(addressData: ValidationAPIOutputAddress): BaseAddress {
  const { add1, add2, state, city, zip, country } = addressData;

  return {
    address1: add1,
    address2: add2 || '',
    city: city || '',
    state: state || '',
    zip,
    country,
  };
}

function shippingAddressToWos(address: CartShippingMethod): Address {
  return {
    name: address.recipient,
    phone: address.phone_number,
    address1: address.street_address,
    address2: address.street_address_secondary || '',
    city: address.city,
    state: address.state_or_region,
    zip: address.postal_code,
    country: address.country,
  };
}

function wosToShippingAddress(
  address: Address
): Omit<CartShippingMethod, 'shipping_method_api_id' | 'name'> {
  return {
    recipient: address.name,
    phone_number: address.phone,
    street_address: address.address1,
    street_address_secondary: address.address2 === '' ? undefined : address.address2,
    city: address.city,
    state_or_region: address.state !== '' ? address.state : address.country,
    country: address.country,
    postal_code: address.zip,
  };
}

/** Validates a partial address object */
function isValidBaseAddress(address: Partial<BaseAddress>): address is BaseAddress {
  return !!address.address1 && !!address.city && !!address.zip && !!address.country;
}

function addressesMatch(suggested: BaseAddress, original: BaseAddress) {
  return Object.keys(suggested).every((key: keyof BaseAddress) => suggested[key] === original[key]);
}

function isContiguousUSState(address: CountryState) {
  const { country, state } = address;
  const excludedStates = ['AE', 'AA', 'AP', 'AK', 'HI'];

  return country === 'US' && !excludedStates.includes(state);
}

function isAlaskaHawaii(address: CountryState) {
  const { country, state } = address;
  const includedStates = ['AK', 'HI'];
  return country === 'US' && includedStates.includes(state);
}

function isUSTerritory(address: CountryState) {
  const includedCountries = ['VI', 'AS', 'GU', 'MP', 'PR', 'MH'];
  const armedForcesStates = ['AE', 'AA', 'AP'];

  return (
    includedCountries.includes(address.country) ||
    (address.country === 'US' && armedForcesStates.includes(address.state))
  );
}

function isCanada(address: CountryState) {
  return address.country === 'CA';
}

const validate = async (address: BaseAddress): Promise<AddressValidationReturnValue> => {
  try {
    const data = await api.validate(wosToValidation(address));
    return [validationToWos(data), data.changed];
  } catch {
    return [{}, false];
  }
};

/**
 * API Documentation
 * https://www.smarty.com/docs/cloud/us-zipcode-api
 */
function handleSmartyAPIResponse(res: SmartyStreetResponse[]) {
  const first = res[0];

  // Since this is just an attempt to autocomplete the city/state, if nothing is found
  // let the user enter their data and handle address validation through Fedex API
  if (first.status || !first.city_states) {
    return null;
  }

  return {
    city: first.city_states[0].city,
    state: first.city_states[0].state_abbreviation,
  };
}

async function zipLookup(zip: string) {
  try {
    const response = await api.zipLookup(zip);
    return handleSmartyAPIResponse(response);
  } catch (error) {
    // We don't need to do any additional handling of these errors
    if (IS_PRODUCTION) {
      reporter.captureMessage('Zip Code Autocomplete Failed');
    }
  }
}

export default {
  format: {
    toShippingAddress: wosToShippingAddress,
    fromShippingAddress: shippingAddressToWos,
  },
  isValidBaseAddress,
  handleSmartyAPIResponse,
  validate,
  zipLookup,
};
